import { inject, Injectable, Injector } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { shouldUseAuth0 } from '../auth/should-use-auth0';
import { firstValueFrom } from 'rxjs';
import { AUTH0_READY } from '../auth/auth0-ready.token';

@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private readonly keycloakService = inject(KeycloakService, { optional: true });
  private readonly injector = inject(Injector);
  private readonly auth0Ready = inject(AUTH0_READY, { optional: true });

  /**
   * True when running inside a parent app's iframe with a known parent origin.
   * When embedded, this app cannot reliably maintain its own auth session
   * (third-party cookie restrictions break silent SSO / token refresh, and a
   * login redirect is blocked by frame-ancestors), so it must obtain tokens
   * from the parent window instead.
   */
  private isEmbedded(): boolean {
    try {
      return window.parent !== window && this.tryGetParentOriginFromQuery() !== null;
    } catch {
      return false;
    }
  }

  private tryGetParentOriginFromQuery(): string | null {
    try {
      const qs = new URLSearchParams(window.location.search);
      const parentUrl =
        qs.get('parentUrl');
      if (!parentUrl) return null;
      return new URL(parentUrl).origin;
    } catch {
      return null;
    }
  }

  private async requestAccessTokenFromParent(timeoutMs: number): Promise<string> {
    const parentOrigin = this.tryGetParentOriginFromQuery();
    if (!parentOrigin) {
      throw new Error('Missing parentUrl/parentOrigin for parent token request');
    }
    if (window.parent === window) {
      throw new Error('Cannot request parent token when not embedded');
    }

    const requestId =
      (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? (crypto.randomUUID() as string)
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const awaitResponse = new Promise<string>((resolve, reject) => {
      const onMessage = (event: MessageEvent) => {
        if (event.origin !== parentOrigin) return;
        const data: any = event.data;
        if (!data || data.action !== 'AUTH_TOKEN' || data.requestId !== requestId) return;
        window.removeEventListener('message', onMessage);
        if (data.error) {
          reject(new Error(String(data.error)));
          return;
        }
        resolve(String(data.token ?? ''));
      };
      window.addEventListener('message', onMessage);

      // Ask the parent for an access token
      window.parent.postMessage(
        { action: 'REQUEST_AUTH_TOKEN', requestId },
        parentOrigin,
      );
    });

    const token = await this.withTimeout(awaitResponse, timeoutMs, 'Parent auth token');
    return this.requireNonEmptyToken(token, 'Parent');
  }

  private requireNonEmptyToken(token: unknown, label: string): string {
    if (typeof token !== 'string' || token.trim().length === 0) {
      throw new Error(`Missing access token from ${label}`);
    }
    return token;
  }

  private async withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    let timeoutId: any;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    });
    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async getAccessToken(): Promise<string> {
    if (shouldUseAuth0()) {
      if (this.auth0Ready !== true) {
        throw new Error('Auth0 is enabled for this domain but Auth0 is not configured.');
      }
      const { AuthService } = await import('@auth0/auth0-angular');
      const auth0Service = this.injector.get(AuthService);

      try {
        const token = await this.withTimeout(
          firstValueFrom(auth0Service.getAccessTokenSilently()),
          8000,
          'Auth0 getAccessTokenSilently()',
        );
        return this.requireNonEmptyToken(token, 'Auth0');
      } catch (err: any) {
        // In iframes, silent auth can fail/hang due to third-party cookie restrictions.
        // Fallback: request a token from the parent window (same user session).
        return await this.requestAccessTokenFromParent(15000);
      }
    }

     if (this.isEmbedded()) {
      try {
        return await this.requestAccessTokenFromParent(15000);
      } catch (err) {
        // The parent didn't hand back a token (e.g. an older parent without a
        // Keycloak token handler, or a transient failure). Fall back to the
        // local session so behavior is no worse than before the parent update.
        const localToken = await this.tryGetLocalKeycloakToken();
        if (localToken) {
          return localToken;
        }
        throw err;
      }
    }

    if (!this.keycloakService) {
      throw new Error('Keycloak is enabled but Keycloak service is not available.');
    }
    // Standalone (top-level) Keycloak session.
    const token = await this.tryGetLocalKeycloakToken();
    return this.requireNonEmptyToken(token, 'Keycloak');
  }

  /**
   * Reads the token from this app's own Keycloak instance, refreshing it first
   * when it is close to expiry. getToken() returns the cached token without
   * refreshing, so updateToken() renews it silently (no re-login) and covers
   * the case where the background onTokenExpired refresh was throttled (e.g. in
   * a backgrounded tab). Returns null when no usable token is available.
   */
  private async tryGetLocalKeycloakToken(): Promise<string | null> {
    if (!this.keycloakService) {
      return null;
    }
    try {
      await this.keycloakService.updateToken(30);
    } catch {
      // Refresh failed (refresh token/session expired); return whatever remains.
    }
    const token = await this.keycloakService.getToken();
    return typeof token === 'string' && token.trim().length > 0 ? token : null;
  }
}

